"use client";

import Link from "next/link";
import Image from "next/image";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import DemoToolbar from "@/components/layout/DemoToolbar";
import { useT } from "@/lib/locale";
import { BRAND } from "@petci/shared-ui";

const NAV_LINKS = [
  { labelKey: "publicNav.status", href: "#status" },
  { labelKey: "publicNav.askPelican", href: "#ask" },
  { labelKey: "publicNav.hurricane", href: "#hurricane" },
  { labelKey: "publicNav.about", href: "#about" },
  { labelKey: "publicNav.help", href: "#help" },
] as const;

type Variant = "landing" | "auth";

export default function PublicHeader({ variant }: { variant: Variant }) {
  const t = useT();
  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-neutral-200">
      <div className="max-w-6xl 2xl:max-w-[1408px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Link href="/" className="flex items-center gap-2.5 group" aria-label={t("brand.homeAria")}>
            {/* D-4: the real pelican, replacing a "PE" monogram roundel. Same
                generated asset the app nav and the mobile header use. */}
            {/* `priority`: the landing header is the first thing a cold visitor
                sees; a lazy brand mark pops in after paint. */}
            <Image
              src="/pelican-mark.png"
              alt=""
              width={28}
              height={28}
              priority
              aria-hidden="true"
              className="flex-shrink-0"
            />
            <span className="font-display font-semibold text-neutral-900 text-sm group-hover:text-brand-primary-text transition-colors">
              {BRAND.full}
            </span>
          </Link>
          <DemoToolbar />
        </div>

        {variant === "landing" && (
          <nav className="hidden lg:flex items-center gap-5" aria-label={t("publicNav.sectionNavLabel")}>
            {NAV_LINKS.map(({ labelKey, href }) => (
              <a
                key={href}
                href={href}
                className="whitespace-nowrap text-sm text-neutral-600 hover:text-brand-primary-text transition-colors"
              >
                {t(labelKey)}
              </a>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2">
          {process.env.NEXT_PUBLIC_SHOW_LANG_SWITCHER !== "false" && (
            <LanguageSwitcher />
          )}
          {variant === "landing" ? (
            <Link href="/home" className="btn-primary text-xs sm:text-sm py-2 px-3 sm:px-4">
              {t("publicNav.enterPreview")}
            </Link>
          ) : (
            <Link
              href="/"
              className="text-sm text-neutral-600 hover:text-brand-primary-text transition-colors"
            >
              <span aria-hidden>←</span> {t("publicNav.home")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
